/**
 * VenueFlow UploaderSection Component
 * 
 * Complete uploader section with:
 * - Title and spec label
 * - Drop area for file selection
 * - File list with progress
 * - Failed list with validation errors
 * 
 * Handles validation, Dropbox upload, and state management.
 */

import React, { useState, useCallback, useRef } from "react";
import DropArea from "./DropArea";
import FileList from "./FileList";
import FailedList from "./FailedList";
import { uploadToDropbox } from "../../services/dropbox";
import { validateFile } from "../../services/mediaInfo";
import { generateFileId, generateCrockfordTimeId } from "../../services/idGenerator";

// ============================================================
// STYLES
// ============================================================
const styles = {
  section: {
    margin: "18px 0 8px",
    padding: "14px 16px",
    borderRadius: 12,
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
  },
  title: {
    margin: "0 0 8px",
    fontSize: 14,
    fontWeight: 600,
    color: "#111827",
  },
  specLabel: {
    marginTop: 2,
    fontSize: 13,
    color: "#6b7280",
    lineHeight: 1.4,
  },
};

// ============================================================
// UPLOADER SECTION COMPONENT
// ============================================================
export function UploaderSection({
  uploadId,
  spec,
  context,
  sessionId,
  submissionTimeId,
  onUploadComplete,
  onUploadFailed,
  onUploadStart,
  onUploadCancel,
  filenamePrefix, // Optional custom prefix (e.g., "TVC" instead of "IPTV-GAA")
  hideHeader = false, // Hide title and spec label when parent provides them
  useSharedId = false, // true = all files share submissionTimeId (LED), false = unique ID per file (Screens)
}) {
  const [files, setFiles] = useState([]);
  const [failed, setFailed] = useState([]);
  const [progress, setProgress] = useState({});
  const abortControllers = useRef(new Map());
  const pendingNames = useRef(new Set());

  // Build status text for title
  const getStatusText = () => {
    const uploaded = files.filter((f) => f.status === "complete").length;
    const required = spec.requiredFiles ?? 1;

    // Only show status for grouped assets (Players Race, External Gates)
    if (spec.contentGroup !== "groupedAssets") {
      return "";
    }

    // Unlimited uploads (e.g. External Gates) — just show count
    if (spec.maxFiles === Infinity || spec.maxFiles == null) {
      if (uploaded === 0) return " — no files uploaded";
      return ` — ${uploaded} file${uploaded !== 1 ? "s" : ""} uploaded`;
    }

    if (uploaded === 0) {
      return ` — 0 uploaded, ${required} remaining`;
    } else if (uploaded < required) {
      return ` — ${uploaded} uploaded, ${required - uploaded} remaining`;
    } else {
      return " — ✅ complete";
    }
  };

  // Build Dropbox path
  const buildDropboxPath = useCallback(
    (prefixedFileName) => {
      const base = `/AFL_Content/_incoming/${context.submitterType}/${context.submitterName}/${sessionId}`;

      // Players Races: flat parent folder with all panels inside (same as LED tiled)
      if (spec.groupFolder) {
        const parentFolder = `${context.submitterShortName}_${submissionTimeId}_${spec.groupFolder}`;
        return `${base}/${parentFolder}/${prefixedFileName}`;
      }

      return `${base}/${prefixedFileName}`;
    },
    [context, sessionId, submissionTimeId, spec]
  );

  // Handle file selection
  const handleFiles = useCallback(
    async (selectedFiles) => {
      for (const file of selectedFiles) {
        const duplicateKey = `${uploadId}::${file.name.toLowerCase()}`;

        // Check for duplicates
        if (pendingNames.current.has(duplicateKey)) {
          setFailed((prev) => [
            ...prev,
            { file, reasons: [`Duplicate filename "${file.name}"`] },
          ]);
          continue;
        }
        pendingNames.current.add(duplicateKey);

        // Check max files limit
        if (typeof spec.maxFiles === "number") {
          const currentCount = files.length;
          if (currentCount >= spec.maxFiles) {
            pendingNames.current.delete(duplicateKey);
            setFailed((prev) => [
              ...prev,
              {
                file,
                reasons: [`This bucket allows a maximum of ${spec.maxFiles} files`],
              },
            ]);
            continue;
          }
        }

        // Build prefixed filename early so we can show the file immediately
        const prefix = filenamePrefix || (spec.prefix ? `${spec.prefix}` : uploadId);
        const fileTimeId = (spec.groupFolder || useSharedId) ? submissionTimeId : generateCrockfordTimeId();
        const prefixedFileName = `${context.submitterShortName}_${fileTimeId}_${prefix}_${file.name}`;
        const fileId = generateFileId(uploadId, prefixedFileName);
        const path = buildDropboxPath(prefixedFileName);

        // Add file immediately with "validating" status so the user sees it
        const validatingEntry = {
          id: fileId,
          uploadId,
          originalFileName: file.name,
          prefixedFileName,
          displayName: `${file.name} → ${prefixedFileName}`,
          status: "validating",
          file,
          inspection: null,
        };
        setFiles((prev) => [...prev, validatingEntry]);

        // Validate file (may take a while for large files via Web Worker)
        let result;
        try {
          result = await validateFile(file, spec);
        } catch (err) {
          console.error("Validation error:", err);
          pendingNames.current.delete(duplicateKey);
          setFiles((prev) => prev.filter((f) => f.id !== fileId));
          setFailed((prev) => [...prev, { file, reasons: ["File validation failed - please try again"] }]);
          onUploadFailed?.({ uploadId, file, reasons: ["File validation failed"] });
          continue;
        }
        
        if (!result.valid) {
          pendingNames.current.delete(duplicateKey);
          setFiles((prev) => prev.filter((f) => f.id !== fileId));
          setFailed((prev) => [...prev, { file, reasons: result.reasons }]);
          onUploadFailed?.({ uploadId, file, reasons: result.reasons });
          continue;
        }

        // Transition to "uploading" status
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileId
              ? {
                  ...f,
                  status: "uploading",
                  inspection: result.info,
                  ...(result.browserFallback && { browserFallback: true }),
                  ...(result.uncheckedFields && { uncheckedFields: result.uncheckedFields }),
                }
              : f
          )
        );
        onUploadStart?.({ uploadId, file: { ...validatingEntry, status: "uploading", inspection: result.info } });

        // Create abort controller
        const abortController = new AbortController();
        abortControllers.current.set(fileId, abortController);

        // Upload to Dropbox
        try {
          const url = await uploadToDropbox(
            file,
            path,
            (pct) => {
              setProgress((prev) => ({ ...prev, [fileId]: pct }));
            },
            abortController
          );

          // Build complete file metadata
          const completeMeta = {
            id: fileId,
            uploadId,
            ledType: spec.ledType,
            label: spec.label,
            contentGroup: spec.contentGroup,
            sourcePath: path,
            sourcePathParent: path.split("/").slice(0, -1).join("/"),
            submissionParent: context.submissionParent,
            originalFileName: file.name,
            prefixedFileName,
            submitterType: context.submitterType,
            submitterName: context.submitterName,
            submitterShortName: context.submitterShortName,
            submissionTimeId: fileTimeId, // Use per-file unique ID
            fileType: file.type,
            size: file.size,
            url,
            inspection: result.info,
            ...(result.browserFallback && { browserFallback: true }),
            ...(result.uncheckedFields && { uncheckedFields: result.uncheckedFields }),
          };

          // Update file status
          setFiles((prev) =>
            prev.map((f) =>
              f.id === fileId
                ? { ...f, status: "complete", url, meta: completeMeta }
                : f
            )
          );

          // Clear progress after a short delay
          setTimeout(() => {
            setProgress((prev) => {
              const next = { ...prev };
              delete next[fileId];
              return next;
            });
          }, 600);

          onUploadComplete?.({ uploadId, file: completeMeta });
        } catch (err) {
          console.error("Upload failed:", err);
          pendingNames.current.delete(duplicateKey);
          setFiles((prev) => prev.filter((f) => f.id !== fileId));
          setFailed((prev) => [
            ...prev,
            { file, reasons: ["Upload cancelled or failed"] },
          ]);
        } finally {
          abortControllers.current.delete(fileId);
        }
      }
    },
    [
      uploadId,
      spec,
      files.length,
      context,
      useSharedId,
      submissionTimeId,
      buildDropboxPath,
      onUploadStart,
      onUploadComplete,
      onUploadFailed,
      filenamePrefix,
    ]
  );

  // Handle file cancellation
  const handleCancel = useCallback(
    (fileId) => {
      const controller = abortControllers.current.get(fileId);
      if (controller) {
        controller.abort();
      }

      const file = files.find((f) => f.id === fileId);
      if (file) {
        const duplicateKey = `${uploadId}::${file.originalFileName.toLowerCase()}`;
        pendingNames.current.delete(duplicateKey);
      }

      setFiles((prev) => prev.filter((f) => f.id !== fileId));
      onUploadCancel?.({ uploadId, fileId });
    },
    [files, uploadId, onUploadCancel]
  );

  // Clear failed list
  const handleClearFailed = useCallback(() => {
    setFailed([]);
  }, []);

  // Build spec label
  const buildSpecLabel = () => {
    const parts = [];

    if (spec.dimensions) {
      parts.push(spec.dimensions.map((d) => `${d.width}×${d.height}`).join(" / "));
    }

    if (spec.videoSpec) {
      if (spec.videoSpec.extensions?.includes("mov")) {
        parts.push("MOV / MP4");
      } else {
        parts.push("MP4");
      }

      if (spec.videoSpec.formats?.includes("AVC")) {
        parts.push("H.264");
      }
      if (spec.videoSpec.formats?.includes("ProRes")) {
        parts.push("ProRes 4444");
      }

      parts.push(`${spec.videoSpec.frameRate}fps`);

      if (spec.videoSpec.duration) {
        parts.push(`${spec.videoSpec.duration}s`);
      }

      parts.push("Looping");
    }

    if (spec.imageSpec?.allowed) {
      if (spec.imageSpec.extensions?.length) {
        parts.push(
          `Static ${spec.imageSpec.extensions.map((e) => e.toUpperCase()).join(" / ")}`
        );
      }
      parts.push(spec.imageSpec.requiresAlpha ? "RGBA" : "RGB");
    }

    return parts.join(" · ");
  };

  return (
    <div style={styles.section} data-upload-id={uploadId}>
      {!hideHeader && (
        <>
          <h3 style={styles.title}>
            {spec.label}
            {getStatusText()}
          </h3>
          <div style={styles.specLabel}>{buildSpecLabel()}</div>
        </>
      )}

      <DropArea onFiles={handleFiles} />

      <FileList files={files} progress={progress} onCancel={handleCancel} />

      <FailedList items={failed} onClear={handleClearFailed} />
    </div>
  );
}

// ============================================================
// GET COMPLETED ASSETS (for form submission)
// ============================================================
UploaderSection.getCompletedAssets = (filesState) => {
  return filesState
    .filter((f) => f.status === "complete" && f.meta)
    .map((f) => f.meta);
};

export default UploaderSection;
